import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import cn from "classnames";
import moment from "moment";
import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { useMeCore } from "../../../cores/me";
import {
  SocialPostStatusOptions,
  SocialStoryStatusOptions,
  getSocialProviderDisplayName,
} from "../../../cores/socialPost/config";
import { SOCIAL_POST_TYPES } from "../../../cores/socialPost/consts/socialPostType.constant";
import { useSocialPostCore } from "../../../cores/socialPost";
import { SocialPost } from "../../../cores/socialPost/interfaces/api/get-social-posts";
import { IconMapper } from "../socialPost/selector/SocialPostSelector";
import { SocialPostPreview } from "./preview/SocialPostPreview";
import {
  getSocialHistoryProviderIconKey,
  socialHistoryBrandSlug,
} from "./socialHistoryProviderIconKey";
import "./SocialHistoryCard.scss";

interface Props {
  post: SocialPost;
  isDarkMode?: boolean;
  /** Grilla: sin vista previa embebida para ahorrar espacio. */
  variant?: "full" | "compact";
  selected?: boolean;
  onToggleSelect?: (postId: string) => void;
}

function excerpt(text: string, max = 220): string {
  const t = (text || "").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max)}…`;
}

export function SocialHistoryCard({
  post,
  isDarkMode = false,
  variant = "full",
  selected = false,
  onToggleSelect,
}: Props) {
  const intl = useIntl();
  const { company } = useMeCore();
  const { deleteSocialPost } = useSocialPostCore();
  const p = post.post;

  const handleDeleteFromHistory = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const ok = window.confirm(
        intl.formatMessage({ id: "app.socialHistory.deleteConfirm" })
      );
      if (!ok || !company?.id) return;
      void deleteSocialPost(company.id, String(p.id));
    },
    [company?.id, deleteSocialPost, intl, p.id]
  );

  const postIdStr = String(p.id);
  const handleToggleSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      onToggleSelect?.(postIdStr);
    },
    [onToggleSelect, postIdStr]
  );

  const networkLabel = getSocialProviderDisplayName(p.socialProvider);
  const iconKey = getSocialHistoryProviderIconKey(p.socialProvider);
  const brandSlug = socialHistoryBrandSlug(iconKey);

  const statusLabel = useMemo(() => {
    const opts =
      p.target === "story" ? SocialStoryStatusOptions : SocialPostStatusOptions;
    return opts.find((o) => o.value === String(p.status))?.label ?? `— (${p.status})`;
  }, [p.status, p.target]);

  const whenLabel = useMemo(() => {
    if (p.postAt) {
      return {
        prefix: intl.formatMessage({ id: "app.socialHistory.publishedAt" }),
        value: moment(p.postAt).format("DD MMM YYYY · HH:mm"),
      };
    }
    if (p.scheduleDate) {
      return {
        prefix: intl.formatMessage({ id: "app.socialHistory.scheduledFor" }),
        value: moment(p.scheduleDate).format("DD MMM YYYY · HH:mm"),
      };
    }
    return { prefix: "", value: "—" };
  }, [p.postAt, p.scheduleDate, intl]);

  const typeLabel = useMemo(() => {
    if (p.target === "story") return null;
    if (p.type === SOCIAL_POST_TYPES.VIDEO) {
      return intl.formatMessage({ id: "app.socialHistory.typeVideo" });
    }
    if (p.type === SOCIAL_POST_TYPES.IMAGE) {
      return intl.formatMessage({ id: "app.socialHistory.typeImage" });
    }
    return intl.formatMessage({ id: "app.socialHistory.typePost" });
  }, [p.target, p.type, intl]);

  const thumb = post.media?.[0];
  const hasThumb = Boolean(thumb?.url);
  const isVideo =
    p.type === SOCIAL_POST_TYPES.VIDEO ||
    (thumb?.type || "").toLowerCase().includes("video");

  const networkIcon = iconKey ? IconMapper[iconKey]?.({ active: true, size: "lg" }) : null;

  return (
    <article
      className={cn(
        "social-history-card",
        `social-history-card--brand-${brandSlug}`,
        {
          "social-history-card--dark": isDarkMode,
          "social-history-card--compact": variant === "compact",
        }
      )}
    >
      <div className="social-history-card__accent" aria-hidden />

      {onToggleSelect ? (
        <div className="social-history-card__select">
          <input
            id={`social-history-card-cb-${postIdStr}`}
            type="checkbox"
            className="social-history-card__select-input"
            checked={selected}
            onChange={handleToggleSelect}
            onClick={(e) => e.stopPropagation()}
            title={intl.formatMessage({ id: "app.socialHistory.selectOneTitle" })}
            aria-label={intl.formatMessage({ id: "app.socialHistory.selectOneTitle" })}
          />
        </div>
      ) : null}

      <button
        type="button"
        className="social-history-card__delete"
        onClick={handleDeleteFromHistory}
        title={intl.formatMessage({ id: "app.socialHistory.deleteTitle" })}
        aria-label={intl.formatMessage({ id: "app.socialHistory.deleteTitle" })}
      >
        <FontAwesomeIcon icon={faTrashAlt} />
      </button>

      <header className="social-history-card__header">
        <div className="social-history-card__thumb" aria-hidden>
          {hasThumb ? (
            isVideo ? (
              <div className="social-history-card__thumb-video">
                <video src={thumb!.url} muted playsInline preload="metadata" />
                <span className="social-history-card__thumb-badge">
                  <FontAwesomeIcon icon={["fas", "play"]} />
                </span>
              </div>
            ) : (
              <img src={thumb!.url} alt="" loading="lazy" />
            )
          ) : (
            <div className="social-history-card__thumb-empty">
              <FontAwesomeIcon icon={["far", "image"]} />
            </div>
          )}
        </div>

        <div className="social-history-card__meta">
          <div className="social-history-card__meta-top">
            <div
              className="social-history-card__network-pill"
              title={intl.formatMessage(
                { id: "app.socialHistory.networkBadgeTitle" },
                { network: networkLabel }
              )}
            >
              <span className="social-history-card__network-pill-icon">
                {networkIcon ?? (
                  <FontAwesomeIcon icon={["fas", "share-nodes"]} size="lg" />
                )}
              </span>
              <span className="social-history-card__network-name">{networkLabel}</span>
            </div>
            <span
              className={cn("social-history-card__badge social-history-card__badge--status", {
                "is-ok": p.status === 1,
                "is-wait": p.status === 0,
                "is-err": p.status === 2,
                "is-warn": p.status === 3 || p.status === 4,
              })}
            >
              {statusLabel}
            </span>
            {p.target === "story" ? (
              <span className="social-history-card__badge social-history-card__badge--muted">
                {intl.formatMessage({ id: "app.socialHistory.targetStory" })}
              </span>
            ) : (
              <>
                {typeLabel ? (
                  <span className="social-history-card__badge social-history-card__badge--muted">
                    {typeLabel}
                  </span>
                ) : null}
                <span className="social-history-card__badge social-history-card__badge--muted">
                  {intl.formatMessage({ id: "app.socialHistory.targetPost" })}
                </span>
              </>
            )}
          </div>

          {(p.publisherName || p.text) && (
            <p className="social-history-card__excerpt">
              {p.publisherName ? (
                <strong className="social-history-card__publisher">
                  {p.publisherName}
                </strong>
              ) : null}
              {p.publisherName && p.text ? " · " : null}
              {excerpt(p.text || "")}
            </p>
          )}

          <div className="social-history-card__dates">
            <FontAwesomeIcon
              icon={["far", "clock"]}
              className="social-history-card__date-icon"
            />
            <span className="social-history-card__date-prefix">{whenLabel.prefix}</span>
            <time dateTime={p.postAt || p.scheduleDate}>{whenLabel.value}</time>
          </div>
        </div>
      </header>

      {variant === "full" && (
        <div className="social-history-card__preview">
          <SocialPostPreview
            provider={p.socialProvider}
            post={post}
            showError={false}
            compact
          />
        </div>
      )}
    </article>
  );
}
